import { useContext } from 'react';

import ScreenHeader from '../components/ScreenHeader.jsx';
import { ScreenChromeContext } from '../help/ScreenChrome.jsx';

// Web stand-in for the app's components/common barrel, for the app screens the web
// renders in place (the Demand Side Response FAQs, and the Watch App help topics
// through their Topic shim). Three building blocks only — Content, Header, Text —
// each mapped onto the web's own screen chrome rather than onto React Native
// layout, so a shared screen looks like a web screen: its Header is the same
// header every web screen has (back link, focused h1, page title) and its Text is
// a paragraph on the app's size / weight scale. Anything else a shared screen asks
// the barrel for is missing here on purpose: it arrives undefined and fails at
// render in the web's tests, which is the cue to decide how that piece should look
// on the web rather than let a React Native layout be guessed at.

// The app's Content is a (possibly scrolling) column; the page scrolls here, so it
// is a plain column and every layout prop is dropped.
export function Content({ children }) {
  return <div className="app-content">{children}</div>;
}

// The app's Header (title + subText). Where its back link goes is not something
// the shared screen knows — it has no web route — so the web wrapper that mounts
// it says, through ScreenChromeContext (see help/ScreenChrome.jsx).
export function Header({ title, subText, children }) {
  const { backTo, backLabel } = useContext(ScreenChromeContext);
  return (
    <ScreenHeader
      title={title}
      sub={subText}
      backTo={backTo}
      backLabel={backLabel}
    >
      {children}
    </ScreenHeader>
  );
}

// The app's Text: size and weight on its own scale (Text/styles.js), line breaks
// in the copy kept (the FAQ answers carry their bullet lists as text). A trailing
// line break — the app's way of spacing a question from its answer — is dropped,
// since the paragraph margin does that here.
export function Text({
  size = 'base',
  weight = 'regular',
  align = 'left',
  children,
}) {
  const text =
    Array.isArray(children) &&
    children.every((child) => typeof child === 'string')
      ? children.join('').replace(/\n+$/, '')
      : children;
  return (
    <p
      className={`app-text app-text--${size} app-text--${weight}`}
      style={align !== 'left' ? { textAlign: align } : undefined}
    >
      {text}
    </p>
  );
}
